import {
  HttpErrorResponse,
  HttpHandler,
  HttpHeaderResponse,
  HttpInterceptor,
  HttpProgressEvent,
  HttpRequest,
  HttpResponse,
  HttpSentEvent,
  HttpStatusCode,
  HttpUserEvent,
} from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, firstValueFrom, from, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { authLog } from 'src/app/auth/auth.handler';
import { Api } from 'src/app/core/common/api';
import { AppSettings } from 'src/app/core/common/app.settings';
import { Auth } from 'src/app/core/common/auth';
import { DataServices } from 'src/app/data/data.services';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private auth = inject(Auth);
  private router = inject(Router);
  private api = inject(Api);
  private data = inject(DataServices);
  private appSettings = inject(AppSettings);

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler
  ): Observable<HttpSentEvent | HttpHeaderResponse | HttpProgressEvent | HttpResponse<unknown> | HttpUserEvent<unknown>> {
    const isApiUrl = req.url.indexOf(this.api.baseUrl) === 0;
    return this.auth.setAuthHeader(req, isApiUrl).pipe(
      switchMap(request => {
        return next.handle(request).pipe(
          catchError(err => {
            if (err instanceof HttpErrorResponse) {
              switch (err.status as HttpStatusCode) {
                case HttpStatusCode.Unauthorized:
                  return from(this.handleUnauthorizedAccess()).pipe(switchMap(() => throwError(() => err as unknown)));
                case HttpStatusCode.Forbidden:
                  // doesn't have the required permissions to call the api or retrieve the data (e.g: PLACEHOLDER_CUSTOMER who has yet complete registration and obtain the CUSTOMER role)
                  authLog(`auth.flow::=>auth.interceptor: forbidden api call, navigating to /signup`, {
                    url: req.url,
                    message: err.message,
                    route: this.router.url,
                  });
                  this.router.navigate(['/signup']);
                  break;
              }
            }
            return throwError(() => err as unknown);
          })
        );
      })
    );
  }
  async handleUnauthorizedAccess() {
    const isAuthenticated = await this.auth.isAuthenticated();
    authLog(`auth.flow::=>auth.interceptor.handleUnauthorizedAccess:`, { isAuthenticated });
    if (isAuthenticated) {
      // already authenticated but access token not authorized, e.g: active staging login session getting used on dev
      try {
        await this.auth.logout();
        await this.data.dispose();
        this.router.navigate(['/']);
      } catch (error) {
        // just in case
      }
    } else {
      await firstValueFrom(this.auth.login());
    }
  }
}
